import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { EmptyKycIllustration, EmptyStateCard } from "../components/EmptyStates";
import {
  ShieldCheck, Check, X, ExternalLink, FileText, Loader2, Eye, Keyboard,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUS_TABS = [
  { key: "in_review", testid: "tab-in-review" },
  { key: "approved", testid: "tab-approved" },
  { key: "rejected", testid: "tab-rejected" },
];

const KIND_LABEL = {
  national_id: "National ID",
  business_registration: "Business registration",
  proof_of_address: "Proof of address",
};

function DocCard({ d, t, onApprove, onReject, busyId, isSelected, onSelect, registerRef,
                  selectableMode, isChecked, onToggleCheck }) {
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const localRef = useRef(null);

  useEffect(() => {
    if (registerRef) registerRef(d.id, localRef.current);
  }, [d.id, registerRef]);

  async function preview() {
    setPreviewing(true);
    try {
      const { data } = await api.get(`/uploads/${d.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener");
      // Give the new tab time to load before revoking
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setPreviewing(false);
    }
  }

  const m = d.merchant || {};
  const busy = busyId === d.id;
  return (
    <div
      ref={localRef}
      onClick={onSelect}
      data-testid={`kyc-doc-${d.id}`}
      data-selected={isSelected || undefined}
      className={`bg-white border rounded-lg p-5 space-y-3 cursor-pointer transition-all ${
        isSelected
          ? "border-[#c15337] ring-2 ring-[#c15337]/25 shadow-[0_4px_18px_rgba(193,83,55,0.18)]"
          : "border-[#114232]/10 hover:border-[#114232]/25"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          {selectableMode && d.status === "in_review" && (
            <input
              type="checkbox"
              checked={isChecked}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggleCheck && onToggleCheck(d.id, e.target.checked)}
              data-testid={`kyc-doc-${d.id}-checkbox`}
              className="mt-1 h-4 w-4 accent-[#c15337] cursor-pointer"
            />
          )}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#c15337]">
              {KIND_LABEL[d.kind] || d.kind.replace(/_/g, " ")}
            </div>
            <div className="font-heading text-base font-bold mt-1 text-[#114232]">{m.business_name || "—"}</div>
            <div className="text-xs text-[#5c6360] mt-0.5">
              {m.owner_name} · <span className="font-mono">{m.phone}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`badge-status ${
            d.status === "approved" ? "badge-success" :
            d.status === "rejected" ? "badge-rejected" : "badge-review"
          }`}>{d.status.replace(/_/g, " ")}</span>
        </div>
      </div>

      <div className="text-xs text-[#5c6360] flex items-center gap-3 flex-wrap">
        <span>{t("kyc_review.submitted")}: <span className="font-mono">{new Date(d.submitted_at).toLocaleString()}</span></span>
        {d.upload && (
          <span className="font-mono">
            {d.upload.content_type} · {(d.upload.size_bytes / 1024).toFixed(1)} KB
          </span>
        )}
      </div>

      {d.review_reason && (
        <div className="bg-[#c15337]/8 border border-[#c15337]/20 rounded-md px-3 py-2 text-xs text-[#8b3a23]">
          {d.review_reason}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button onClick={preview} disabled={previewing}
                data-testid={`kyc-doc-${d.id}-preview`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-[#114232]/15 rounded-md bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors disabled:opacity-60">
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          {t("kyc_review.preview")}
        </button>
        {m.id && (
          <Link to={`/merchants/${m.id}`}
                data-testid={`kyc-doc-${d.id}-view-merchant`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-[#114232]/15 rounded-md bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors">
            <ExternalLink className="w-3.5 h-3.5" /> {t("kyc_review.view_merchant")}
          </Link>
        )}
        {d.status === "in_review" && (
          <>
            <Button onClick={() => onApprove(d.id)} disabled={busy}
                    data-testid={`kyc-doc-${d.id}-approve`}
                    className="btn-baobab rounded-full px-4 h-8 text-xs hover:bg-[var(--baobab-hover)]">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              {t("kyc_review.approve")}
            </Button>
            <Button onClick={() => setShowReject((v) => !v)} disabled={busy}
                    data-testid={`kyc-doc-${d.id}-reject-toggle`}
                    className="btn-terracotta rounded-full px-4 h-8 text-xs hover:bg-[var(--terracotta-hover)]">
              <X className="w-3.5 h-3.5 mr-1" /> {t("kyc_review.reject")}
            </Button>
          </>
        )}
      </div>

      {showReject && d.status === "in_review" && (
        <div className="space-y-2 border-t border-[#114232]/10 pt-3" data-testid={`kyc-doc-${d.id}-reject-form`}>
          <Textarea rows={2} value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t("kyc_review.reject_reason_placeholder")}
                    className="border-[#114232]/15"
                    data-testid={`kyc-doc-${d.id}-reason`} />
          <Button onClick={() => { onReject(d.id, reason); setShowReject(false); }}
                  disabled={busy || !reason.trim()}
                  data-testid={`kyc-doc-${d.id}-reject-submit`}
                  className="btn-terracotta rounded-full px-4 h-8 text-xs hover:bg-[var(--terracotta-hover)]">
            {t("kyc_review.reject")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function KycReview() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("in_review");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [checked, setChecked] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const cardRefs = useRef({});

  function registerRef(id, el) { cardRefs.current[id] = el; }

  function toggleCheck(id, on) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  function toggleSelectAll() {
    const inReviewIds = items.filter((d) => d.status === "in_review").map((d) => d.id);
    if (checked.size === inReviewIds.length && inReviewIds.length > 0) {
      setChecked(new Set());
    } else {
      setChecked(new Set(inReviewIds));
    }
  }

  function load() {
    api.get("/admin/kyc-documents", { params: { status: tab } })
      .then((r) => {
        setItems(r.data.documents || []);
        setSelectedIdx(0);
        setChecked(new Set());
      });
  }
  useEffect(load, [tab]);

  async function bulkDecide(decision) {
    if (checked.size === 0) {
      toast.message("Nothing selected", { description: "Tick a few documents first." });
      return;
    }
    let reason = null;
    if (decision === "reject") {
      reason = window.prompt(t("kyc_review.reject_reason_placeholder") || "Reason for rejection (applied to all):");
      if (!reason || !reason.trim()) return;
    }
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/admin/kyc-documents/bulk-decide", {
        document_ids: Array.from(checked),
        decision,
        reason: reason || undefined,
      });
      const okCount = (r.results || []).filter((x) => x.ok).length;
      toast.success(`Bulk ${decision}d ${okCount} document${okCount === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBulkBusy(false);
    }
  }

  // Keyboard shortcuts: A = approve, R = reject (prompt), ↓/→ next, ↑/← prev, Esc unfocus
  useEffect(() => {
    function onKey(e) {
      // Don't hijack typing in inputs/textareas
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const current = items[selectedIdx];
      if (!current && !["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return;

      if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === "j") {
        e.preventDefault();
        setSelectedIdx((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft" || e.key === "k") {
        e.preventDefault();
        setSelectedIdx((i) => Math.max(0, i - 1));
      } else if ((e.key === "a" || e.key === "A") && tab === "in_review" && current) {
        e.preventDefault();
        approve(current.id);
      } else if ((e.key === "r" || e.key === "R") && tab === "in_review" && current) {
        e.preventDefault();
        const reason = window.prompt(t("kyc_review.reject_reason_placeholder") || "Reason for rejection:");
        if (reason && reason.trim()) reject(current.id, reason.trim());
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedIdx, tab]);

  // Scroll selected card into view
  useEffect(() => {
    const id = items[selectedIdx]?.id;
    const el = id ? cardRefs.current[id] : null;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedIdx, items]);

  async function approve(id) {
    setBusyId(id);
    try {
      await api.patch(`/admin/kyc-documents/${id}`, { decision: "approve" });
      toast.success(t("kyc_review.approved_toast"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  }

  async function reject(id, reason) {
    setBusyId(id);
    try {
      await api.patch(`/admin/kyc-documents/${id}`, { decision: "reject", reason });
      toast.success(t("kyc_review.rejected_toast"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  }

  return (
    <div className="p-8 space-y-6" data-testid="kyc-review-page">
      <Toaster position="top-right" />
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c15337]">Compliance</div>
        <h1 className="font-heading text-3xl font-bold mt-1 flex items-center gap-2 text-[#114232]">
          <ShieldCheck className="w-7 h-7" /> {t("kyc_review.title")}
        </h1>
        <p className="text-sm text-[#5c6360] mt-1">{t("kyc_review.subtitle")}</p>
        <div
          className="mt-3 inline-flex items-center gap-2 text-[11px] text-[#5c6360] bg-white border border-[#114232]/10 rounded-full px-3 py-1.5"
          data-testid="kyc-shortcut-help"
        >
          <Keyboard className="w-3.5 h-3.5" />
          <span><kbd className="px-1.5 py-0.5 bg-[#114232]/8 rounded text-[10px] font-mono">A</kbd> approve · </span>
          <span><kbd className="px-1.5 py-0.5 bg-[#114232]/8 rounded text-[10px] font-mono">R</kbd> reject · </span>
          <span><kbd className="px-1.5 py-0.5 bg-[#114232]/8 rounded text-[10px] font-mono">↑↓</kbd> navigate</span>
        </div>
      </header>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 text-xs">
          {STATUS_TABS.map((s) => (
            <button key={s.key} onClick={() => setTab(s.key)}
                    data-testid={s.testid}
                    className={`px-4 py-2 rounded-full uppercase tracking-wider font-semibold transition-colors ${
                      tab === s.key
                        ? "bg-[#114232] text-[#f4f2eb]"
                        : "bg-white border border-[#114232]/15 text-[#5c6360] hover:text-[#114232]"
                    }`}>
              {t(`kyc_review.tab_${s.key}`)}
            </button>
          ))}
        </div>
        {tab === "in_review" && items.length > 0 && (
          <div className="flex items-center gap-2 text-xs" data-testid="kyc-bulk-controls">
            <button
              onClick={toggleSelectAll}
              data-testid="kyc-select-all"
              className="px-3 py-1.5 rounded-md border border-[#114232]/15 bg-white hover:bg-[#114232]/5 text-[#114232] uppercase tracking-wider font-semibold"
            >
              {checked.size === items.filter((d) => d.status === "in_review").length && items.length > 0
                ? "Clear" : "Select all"}
            </button>
            <span className="text-[#5c6360] font-mono" data-testid="kyc-bulk-count">
              {checked.size} selected
            </span>
            <Button
              onClick={() => bulkDecide("approve")}
              disabled={bulkBusy || checked.size === 0}
              data-testid="kyc-bulk-approve"
              className="btn-baobab rounded-full px-4 h-8 text-xs hover:bg-[var(--baobab-hover)]"
            >
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Bulk approve
            </Button>
            <Button
              onClick={() => bulkDecide("reject")}
              disabled={bulkBusy || checked.size === 0}
              data-testid="kyc-bulk-reject"
              className="btn-terracotta rounded-full px-4 h-8 text-xs hover:bg-[var(--terracotta-hover)]"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Bulk reject
            </Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyStateCard
          testid="kyc-empty"
          illustration={<EmptyKycIllustration className="w-full h-full" />}
          title={
            tab === "in_review"
              ? t("kyc_review.empty_in_review")
              : t("kyc_review.no_documents")
          }
          body={
            tab === "in_review"
              ? "When merchants submit national IDs, business registrations, or proof of address, you'll see them here."
              : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="kyc-list">
          {items.map((d, i) => (
            <DocCard
              key={d.id} d={d} t={t}
              onApprove={approve} onReject={reject} busyId={busyId}
              isSelected={i === selectedIdx}
              onSelect={() => setSelectedIdx(i)}
              registerRef={registerRef}
              selectableMode={tab === "in_review"}
              isChecked={checked.has(d.id)}
              onToggleCheck={toggleCheck}
            />
          ))}
        </div>
      )}
    </div>
  );
}
